// src/components/pages/Contact/ContactUsPage.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { alpha } from "@mui/material/styles";
import { getPrimaryColor } from "../../GlobalFunction/Theme";
import { getLangFromCookie, LANG_COOKIE } from "../../../Utils/LangCookie";

// ✅ SAME submit behavior as ProductConsultForm
import { ProductConsultingAPI } from "../../../api/productConsulting";

// ✅ Auth (prefill + suggestions like ProductConsultForm)
import { useAuth } from "../Auth/AuthContext"; // adjust if your AuthContext path differs

// ✅ Success dialog (reusable)
import SuccessSubmitDialog from "../../GlobalFunction/SuccessSubmit";
import SehoLoadingSpinner from "../../GlobalFunction/SehoLoadingSpinner";

/* ---------- LEFT SIDE i18n (keep your original info area text) ---------- */
const STRINGS = {
  ZH: {
    pageTitle: "聯絡我們",
    addLabel: "地址",
    telLabel: "電話",
    mailLabel: "信箱",
    introLine1: "若您有任何問題或建議，請聯絡我們，",
    introLine2: "我們會盡快處理，謝謝！",
  },
  EN: {
    pageTitle: "Contact Us",
    addLabel: "Address",
    telLabel: "Phone",
    mailLabel: "Email",
    introLine1: "Please contact us if you need any help.",
    introLine2: "We’ll reply soon. Thank you!",
  },
  JP: {
    pageTitle: "お問い合わせ",
    addLabel: "所在地",
    telLabel: "電話番号",
    mailLabel: "メールアドレス",
    introLine1:
      "お問い合わせください。",
    introLine2:
      "速やかに対応いたします。",
  },
};

/* ---------- FORM i18n (MATCH ProductConsultForm) ---------- */
const I18N = {
  ZH: {
    title: "專業諮詢",
    labels: {
      company: "公司名稱",
      name: "姓名/職稱",
      phone: "電話號碼",
      email: "電子郵件",
      subject: "諮詢主題",
      detail: "諮詢詳情",
    },
    requiredFlag: "【必填】",
    requiredField: "此欄為必填",
    agreeLabel: "我同意以上內容",
    agreeError: "請勾選同意以上內容",
    notices: [
      "我們將根據《個人資訊保護法》等法規妥善處理您的個人資訊與諮詢內容。",
      "請確認您的電子郵件地址正確，以便我們回覆您的諮詢。",
      "工作日（國定假日除外）09:00–18:00回覆。",
    ],
    submit: "送出",
    submitted: "已送出您的諮詢，我們將盡快與您聯繫！",
    submitFailed: "送出失敗，請稍後再試或聯絡客服。",
    submitting: "送出中…",
  },

  EN: {
    title: "Professional Consultation",
    labels: {
      company: "Company Name",
      name: "name/job title",
      phone: "Phone Number",
      email: "E-mail Address",
      subject: "Subject",
      detail: "Inquiry Details",
    },
    requiredFlag: "[Required]",
    requiredField: "This field is required.",
    agreeLabel: "I agree to the above terms.",
    agreeError: "Please check the box to agree to the terms.",
    notices: [
      "We will handle your personal information and inquiry in accordance with the Personal Data Protection Act and related regulations.",
      "Please confirm that your e-mail address is correct so we can reply to you.",
      "Replies will be provided on business days (excluding national holidays) between 09:00–18:00.",
    ],
    submit: "Submit",
    submitted: "Your inquiry has been sent. We will get back to you shortly.",
    submitFailed: "Submission failed. Please try again later.",
    submitting: "Submitting...",
  },

  JP: {
    title: "専門相談のお申し込み",
    labels: {
      company: "会社名",
      name: "氏名 / 役職",
      phone: "電話番号",
      email: "メールアドレス",
      subject: "ご相談内容（件名）",
      detail: "ご相談の詳細",
    },
    requiredFlag: "【必須】",
    requiredField: "この項目は必須です。",
    agreeLabel: "上記の内容に同意します。",
    agreeError: "次へ進むには、規約にご同意いただく必要があります。",
    notices: [
      "お客様からいただいた個人情報およびお問い合わせ内容は、個人情報保護法等の法令に基づき、適切に管理いたします。",
      "うまく返信が届くよう、メールアドレスに誤りがないようにご確認ください。",
      "営業時間：9:00〜18:00（土日祝日を除く）",
    ],
    submit: "送信する",
    submitted: "お問い合わせには、翌営業日以降に順次返信いたします。",
    submitFailed:
      "送信に失敗しました。しばらくしてからもう一度お試しください。",
    submitting: "送信中…",
  },
};

/* ---------- Form layout helpers (same as ProductConsultForm) ---------- */
const LabelCell = ({ children, required, requiredLabel }) => (
  <Box
    sx={(t) => ({
      bgcolor: t.palette.mode === "light" ? "grey.50" : alpha("#fff", 0.04),
      borderRight: { md: `1px solid ${t.palette.divider}` },
      p: 1.25,
      display: "flex",
      alignItems: "center",
      minHeight: 56,
    })}
  >
    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
      {children}{" "}
      {required && (
        <Typography
          component="span"
          color="error.main"
          sx={{ fontWeight: 700 }}
        >
          {requiredLabel}
        </Typography>
      )}
    </Typography>
  </Box>
);

const Row = ({ label, required, requiredLabel, children }) => (
  <Box
    sx={(t) => ({
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "240px 1fr" },
      borderBottom: `1px solid ${t.palette.divider}`,
      "&:first-of-type": { borderTop: `1px solid ${t.palette.divider}` },
    })}
  >
    <LabelCell required={required} requiredLabel={requiredLabel}>
      {label}
    </LabelCell>
    <Box sx={{ p: 1.25 }}>{children}</Box>
  </Box>
);

function normalizeLang(lang) {
  const s = String(lang || "")
    .toUpperCase()
    .trim();
  return s === "EN" || s === "JP" || s === "ZH" ? s : "ZH";
}

const getCurrentLang = () =>
  normalizeLang(
    getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH",
  );

const LINE_BARCODE_SRC = "/img/social/LINE.jpg";

function isBlank(v) {
  return !String(v ?? "").trim();
}

function uniqNonBlank(list) {
  const out = [];
  const seen = new Set();
  for (const v of list || []) {
    const s = String(v ?? "").trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

function pickProfile(user) {
  const name = String(user?.name || user?.viewer?.name || "").trim();
  const email = String(user?.email || user?.viewer?.email || "").trim();
  const phone = String(
    user?.phoneNumber || user?.viewer?.phoneNumber || "",
  ).trim();
  return { name, email, phone };
}

function buildTelHref(phone) {
  const normalized = String(phone ?? "")
    .trim()
    .replace(/[^\d+]/g, "");

  return normalized ? `tel:${normalized}` : "";
}

export default function ContactUsPage({
  mode = "b2b",
  company = "SEHO",
  phone = "+886-2-2609-6867",
  // phone = "+886-2-2609-6867",
  email = "service@sehopro.com",
  fontfamily = "LINE Sans TW",
}) {
  const primary = getPrimaryColor(mode);

  /* ---------------- language sync (same style as your app) ---------------- */
  const [lang, setLang] = useState(getCurrentLang());

  useEffect(() => {
    const cookieVal = getLangFromCookie();
    const lsVal = localStorage.getItem(LANG_COOKIE);
    if (!cookieVal && lsVal) {
      try {
        document.cookie = `${LANG_COOKIE}=${lsVal};path=/;max-age=31536000`;
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    const onLangChange = () => setLang(getCurrentLang());
    window.addEventListener("app:languageChanged", onLangChange);
    window.addEventListener("storage", onLangChange);
    return () => {
      window.removeEventListener("app:languageChanged", onLangChange);
      window.removeEventListener("storage", onLangChange);
    };
  }, []);

  const tLeft = STRINGS[lang] || STRINGS.ZH;
  const dict = I18N[lang] || I18N.ZH;

  const ADDRESS = {
    ZH: "台灣新北市林口區文明街183號",
    EN: "No. 183, Wenming Street, Linkou District, New Taipei City, Taiwan",
    JP: "台湾・新北市林口区文明街183番地",
  };

  /* ---------------- auth (for prefill like ProductConsultForm) ---------------- */
  const { user, loading: authLoading } = useAuth();
  const isLoggedIn = !!user && !authLoading;

  const [suggest, setSuggest] = useState({ name: [], email: [], phone: [] });

  /* ---------------- form state (EXACT SAME SHAPE) ---------------- */
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    detail: "",
    agree: false,
  });

  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [isSending, setIsSending] = useState(false);
  const [serverError, setServerError] = useState("");

  const [successOpen, setSuccessOpen] = useState(false);

  const requiredKeys = useMemo(
    () => ["company", "name", "phone", "email", "detail"],
    [],
  );

  const trim = (v) => String(v ?? "").trim();

  const validate = (nextForm) => {
    const next = {};
    for (const k of requiredKeys) {
      if (!trim(nextForm[k])) next[k] = true;
    }
    if (!nextForm.agree) next.agree = true;
    return next;
  };

  // Prefill + suggestions (same as ProductConsultForm)
  useEffect(() => {
    if (!isLoggedIn) {
      setSuggest({ name: [], email: [], phone: [] });
      return;
    }

    const { name, email, phone } = pickProfile(user);

    setSuggest({
      name: uniqNonBlank([name]),
      email: uniqNonBlank([email]),
      phone: uniqNonBlank([phone]),
    });

    setForm((prev) => {
      const next = { ...prev };
      if (isBlank(next.name) && name) next.name = name;
      if (isBlank(next.email) && email) next.email = email;
      if (isBlank(next.phone) && phone) next.phone = phone;
      return next;
    });
  }, [isLoggedIn, user]);

  // keep error flags updated on language changes (same as ProductConsultForm)
  useEffect(() => {
    if (!submitAttempted) return;
    setErrors(validate(form));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const update = (k) => (e) => {
    const value = e.target.value;

    setForm((prev) => {
      const nextForm = { ...prev, [k]: value };

      if (submitAttempted) {
        setErrors(validate(nextForm));
      } else {
        setErrors((prevErr) => {
          if (!prevErr?.[k]) return prevErr;
          if (!trim(value)) return prevErr;
          const cp = { ...prevErr };
          delete cp[k];
          return cp;
        });
      }

      return nextForm;
    });
  };

  const onToggleAgree = (e) => {
    const checked = !!e.target.checked;

    setForm((prev) => {
      const nextForm = { ...prev, agree: checked };

      if (submitAttempted) {
        setErrors(validate(nextForm));
      } else {
        setErrors((prevErr) => {
          if (!prevErr?.agree) return prevErr;
          if (!checked) return prevErr;
          const cp = { ...prevErr };
          delete cp.agree;
          return cp;
        });
      }

      return nextForm;
    });
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (isSending) return; // ✅ double-submit guard

    setServerError("");
    setSubmitAttempted(true);

    const next = validate(form);
    setErrors(next);
    if (Object.keys(next).length) return;

    setIsSending(true);
    try {
      // ✅ SAME AS ProductConsultForm
      await ProductConsultingAPI.create(form, { lang, mode });

      // reset
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        subject: "",
        detail: "",
        agree: false,
      });
      setErrors({});
      setSubmitAttempted(false);

      setSuccessOpen(true);
    } catch (err) {
      if (err?.field) setErrors((prev) => ({ ...prev, [err.field]: true }));
      setServerError(err?.message || dict.submitFailed);
    } finally {
      setIsSending(false);
    }
  };

  const tfCommon = {
    size: "small",
    fullWidth: true,
    InputLabelProps: { shrink: true },
    disabled: isSending,
  };

  const mapSrc =
    "https://maps.google.com/maps?q=25.0728856,121.3614029&z=17&hl=zh-TW&output=embed";

  const phoneHref = buildTelHref(phone);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const preloadId = "seho-line-barcode-preload";

    if (document.getElementById(preloadId)) {
      return undefined;
    }

    const link = document.createElement("link");
    link.id = preloadId;
    link.rel = "preload";
    link.as = "image";
    link.href = LINE_BARCODE_SRC;
    link.fetchPriority = "high";

    document.head.appendChild(link);

    return () => {
      const currentLink = document.getElementById(preloadId);

      if (currentLink) {
        currentLink.remove();
      }
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "center",
        alignItems: "flex-start",
        gap: { xs: 3, md: 4 },
        px: { xs: 2, md: "2%" },
        py: { xs: 2.5, md: 6 },
        maxWidth: "1280px",
        mx: "auto",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      {/* LEFT COLUMN: Info + Map (unchanged layout) */}
      <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 45%" } }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 0, md: 2 },
            overflow: "hidden",
            bgcolor: primary,
            color: "#fff",
            mx: { xs: -2, md: 0 },
            width: { xs: "100vw", md: "auto" },
          }}
        >
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              position: "relative",
            }}
          >
            <Typography
              variant="overline"
              sx={{ opacity: 1 }}
              fontSize={{ xs: 16, sm: 30, md: 20 }}
              fontFamily={fontfamily}
              fontWeight={700}
            >
              {tLeft.addLabel}
            </Typography>
            <Typography
              sx={{ fontSize: { xs: 14, sm: 25, md: 16 }, mb: 2 }}
              fontFamily={fontfamily}
              fontWeight={400}
            >
              {ADDRESS[lang] || ADDRESS.ZH}
            </Typography>

            <Typography
              variant="overline"
              sx={{ opacity: 1 }}
              fontSize={{ xs: 16, sm: 30, md: 20 }}
              fontFamily={fontfamily}
              fontWeight={700}
            >
              {tLeft.telLabel}
            </Typography>
            <Typography
              component={phoneHref ? "a" : "p"}
              href={phoneHref || undefined}
              aria-label={phoneHref ? `Call ${phone}` : undefined}
              sx={{
                fontSize: { xs: 14, sm: 25, md: 16 },
                mb: 2,
                color: "inherit",
                textDecoration: "none",
                display: "block",
                width: "fit-content",
                "&:hover": {
                  textDecoration: phoneHref ? "underline" : "none",
                },
              }}
              fontFamily={fontfamily}
              fontWeight={400}
            >
              {phone}
            </Typography>

            <Typography
              variant="overline"
              sx={{ opacity: 1 }}
              fontSize={{ xs: 16, sm: 30, md: 20 }}
              fontFamily={fontfamily}
              fontWeight={700}
            >
              {tLeft.mailLabel}
            </Typography>
            <Typography
              sx={{ fontSize: { xs: 14, sm: 25, md: 16 } }}
              fontFamily={fontfamily}
              fontWeight={400}
            >
              {email}
            </Typography>

            <Box
              component="img"
              src={LINE_BARCODE_SRC}
              alt="LINE barcode"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
              sx={{
                position: "absolute",
                display: "block",
                top: { xs: 132, sm: 280, md: 250, lg: 250 },
                right: { xs: 26, sm: 55, md: 30, lg: 30 },
                width: { xs: 116, sm: 220, md: 150, lg: 150 },
                height: "auto",
                objectFit: "contain",
                bgcolor: "transparent",
                border: "none",
                borderRadius: 2,
                boxShadow: "none",
                pointerEvents: "none",
                userSelect: "none",
                zIndex: 1,
              }}
            />

            <Typography
              sx={{ mt: 3, opacity: 1, lineHeight: 1.9 }}
              fontSize={{ xs: 16, sm: 25, md: 20 }}
              fontFamily={fontfamily}
              fontWeight={400}
              whiteSpace="pre-line"
            >
              {tLeft.introLine1}
              <br />
              {tLeft.introLine2}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            mt: { xs: 2.5, md: 3 },
            borderRadius: { xs: 0, md: 2 },
            overflow: "hidden",
            border: (th) => `1px solid ${th.palette.divider}`,
            mx: { xs: -2, md: 0 },
            width: { xs: "100vw", md: "auto" },
          }}
        >
          <Box sx={{ position: "relative", pt: "60%" }}>
            <Box
              component="iframe"
              title="google-map"
              src={mapSrc}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* RIGHT COLUMN: Form (NOW MATCHES ProductConsultForm input fields) */}
      <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 45%" } }}>
        {/* SEHO loading screen while API is running */}
        <Backdrop
          open={isSending}
          sx={{
            zIndex: (t) => t.zIndex.modal + 10,
            backgroundColor: "rgba(6, 17, 21, 0.76)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            pointerEvents: "auto",
            touchAction: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2.25,
              px: { xs: 3, sm: 4 },
              py: { xs: 3.2, sm: 3.6 },
              borderRadius: 4,
              minWidth: { xs: 240, sm: 280 },
              maxWidth: "90vw",
              position: "relative",
              bgcolor: "rgba(255,255,255,0.74)",
              border: "1px solid rgba(13,158,175,0.18)",
              boxShadow:
                "0 18px 48px rgba(13,158,175,0.12), 0 8px 18px rgba(0,0,0,0.06)",
              backdropFilter: "blur(14px)",
            }}
          >
            <SehoLoadingSpinner variant="overlay" />

            <Box
              data-nosnippet
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.6,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  color: "#4E5A61",
                }}
              >
                {dict.submitting}
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  letterSpacing: 0.2,
                  color: "#0D9EAF",
                }}
              >
                {tLeft.pageTitle}
              </Typography>
            </Box>

            <Box
              sx={{
                mt: 0.4,
                width: "100%",
                height: 4,
                borderRadius: 999,
                overflow: "hidden",
                background: "rgba(13,158,175,0.08)",
              }}
            >
              <Box
                sx={{
                  width: "45%",
                  height: "100%",
                  borderRadius: 999,
                  background:
                    "linear-gradient(90deg, #0D9EAF 0%, #D69A3A 100%)",
                  animation: "sehoLoadingBar 1.8s ease-in-out infinite",
                  transformOrigin: "left center",
                  "@keyframes sehoLoadingBar": {
                    "0%": {
                      transform: "translateX(-100%) scaleX(0.75)",
                    },
                    "50%": {
                      transform: "translateX(70%) scaleX(1)",
                    },
                    "100%": {
                      transform: "translateX(230%) scaleX(0.75)",
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Backdrop>

        {/* ✅ Success dialog (same as ProductConsultForm) */}
        <SuccessSubmitDialog
          open={successOpen}
          onClose={() => setSuccessOpen(false)}
          primaryHref={mode === "b2b" ? "/b2b/商品資訊" : "/b2c/全站商品"}
          primaryLabel={{
            ZH: "查看商品",
            EN: "Browse Products",
            JP: "商品を見る",
          }}
          secondaryHref="/"
          secondaryLabel={{
            ZH: "回到首頁",
            EN: "Back to Home",
            JP: "ホームへ戻る",
          }}
        />

        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
          {tLeft.pageTitle}
        </Typography>

        {/* ✅ EXACT SAME input block structure as ProductConsultForm */}
        <Paper
          component="form"
          onSubmit={handleSubmit}
          noValidate
          variant="outlined"
          sx={{ borderRadius: 1.5, overflow: "hidden" }}
        >
          <Row
            label={dict.labels.company}
            required
            requiredLabel={dict.requiredFlag}
          >
            <TextField
              {...tfCommon}
              value={form.company}
              onChange={update("company")}
              error={!!errors.company}
              helperText={errors.company ? dict.requiredField : " "}
            />
          </Row>

          <Row
            label={dict.labels.name}
            required
            requiredLabel={dict.requiredFlag}
          >
            <Autocomplete
              freeSolo
              options={isLoggedIn ? suggest.name : []}
              value={form.name || ""}
              onInputChange={(e, v) => setForm((s) => ({ ...s, name: v }))}
              onChange={(e, v) => setForm((s) => ({ ...s, name: v || "" }))}
              disabled={isSending}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...tfCommon}
                  error={!!errors.name}
                  helperText={errors.name ? dict.requiredField : " "}
                />
              )}
            />
          </Row>

          <Row
            label={dict.labels.phone}
            required
            requiredLabel={dict.requiredFlag}
          >
            <Autocomplete
              freeSolo
              options={isLoggedIn ? suggest.phone : []}
              value={form.phone || ""}
              onInputChange={(e, v) => setForm((s) => ({ ...s, phone: v }))}
              onChange={(e, v) => setForm((s) => ({ ...s, phone: v || "" }))}
              disabled={isSending}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...tfCommon}
                  error={!!errors.phone}
                  helperText={errors.phone ? dict.requiredField : " "}
                />
              )}
            />
          </Row>

          <Row
            label={dict.labels.email}
            required
            requiredLabel={dict.requiredFlag}
          >
            <Autocomplete
              freeSolo
              options={isLoggedIn ? suggest.email : []}
              value={form.email || ""}
              onInputChange={(e, v) => setForm((s) => ({ ...s, email: v }))}
              onChange={(e, v) => setForm((s) => ({ ...s, email: v || "" }))}
              disabled={isSending}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...tfCommon}
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email ? dict.requiredField : " "}
                />
              )}
            />
          </Row>

          <Row label={dict.labels.subject}>
            <TextField
              {...tfCommon}
              value={form.subject}
              onChange={update("subject")}
            />
          </Row>

          <Row
            label={dict.labels.detail}
            required
            requiredLabel={dict.requiredFlag}
          >
            <TextField
              {...tfCommon}
              multiline
              minRows={6}
              value={form.detail}
              onChange={update("detail")}
              error={!!errors.detail}
              helperText={errors.detail ? dict.requiredField : " "}
            />
          </Row>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSending}
              sx={{
                px: 6,
                py: 1,
                fontWeight: 700,
                bgcolor: primary,
                "&:hover": { bgcolor: alpha(primary, 0.85) },
              }}
            >
              {isSending ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  <span>{dict.submit}</span>
                </Box>
              ) : (
                dict.submit
              )}
            </Button>
          </Box>
        </Paper>

        {/* Notices + consent (same as ProductConsultForm) */}
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            mt: 2.5,
            borderRadius: 1.5,
            borderColor: (t) => alpha(primary, 0.28),
          }}
        >
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
            {dict.notices.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </Paper>

        <Box sx={{ textAlign: "center", my: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.agree}
                onChange={onToggleAgree}
                disabled={isSending}
              />
            }
            label={
              <Typography sx={{ fontWeight: 600 }}>
                {dict.agreeLabel}
              </Typography>
            }
          />

          {errors.agree && (
            <Typography variant="caption" color="error" display="block">
              {dict.agreeError}
            </Typography>
          )}
        </Box>

        {!!serverError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {serverError}
          </Alert>
        )}
      </Box>
    </Box>
  );
}
